import React from 'react';
import { UserContext } from '../../context/UserContext';
import { CompanyContext } from '../../context/CompanyContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { GroupContext } from '../../context/GroupContext';
import { Box, Grid } from '@mui/material';
import ResourceCentreContext from '../../components/resource_centre/home/ResourceCentreContext';
import ResourceCentreDisplayGrid from '../../components/resource_centre/home/ResourceCentreDisplayGrid';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { Resource, ResourceMetadata } from '@esg/esg-global-types';
import { getResources } from '../../graphql/services/resource.service';
import { getResourceMetadata } from '../../graphql/services/resource_metadata.service';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

export interface ResourceFilterContext {
  text: string;
  categories: Array<string>;
  tags: Array<string>;
  types: Array<string>;
}

const initial_resource_filter_context: ResourceFilterContext = {
  text: '',
  categories: [],
  tags: [],
  types: []
};

/**
 * View for displaying the entry point for the Resource Centre with variable filters and links to resources.
 * @returns {JSX.Element}
 */
const ResourceCentreHome = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Resource Centre', 'N/A', group.id, company.id);

  const [access, setAccess] = React.useState<ViewAccess>(null);
  const [resources, setResources] = React.useState<Array<Resource>>([]);
  const [resourcesMetadata, setResourcesMetadata] = React.useState<Array<ResourceMetadata>>([]);
  const [resourceFilters, setResourceFilters] = React.useState<ResourceFilterContext>(
    initial_resource_filter_context
  );
  const [resourcesLoading, setResourcesLoading] = React.useState<boolean>(false);

  // Filter resources list based on current filters
  const filtered_resources: Array<Resource> = resources.filter((resource: Resource) => {
    const resource_filter_options: Set<string> = new Set(
      [
        ...(resource.categories ?? []),
        ...(resource.tags ?? []),
        ...(resource.type ? [resource.type] : [])
      ].map((filter_option: string) => filter_option.toLowerCase())
    );
    return (
      (!resource.transcoding_status || resource.transcoding_status === 'complete') &&
      (resourceFilters.categories.length < 1 ||
        resourceFilters.categories.some((category: string) =>
          resource_filter_options.has(category.toLowerCase())
        )) &&
      (resourceFilters.tags.length < 1 ||
        resourceFilters.tags.some((tag: string) =>
          resource_filter_options.has(tag.toLowerCase())
        )) &&
      (resourceFilters.types.length < 1 ||
        resourceFilters.types.some((type: string) =>
          resource_filter_options.has(type.toLowerCase())
        )) &&
      (!resourceFilters.text.length ||
        resource.name.toLowerCase().includes(resourceFilters.text.toLowerCase()) ||
        (resource.description
          ? resource.description.toLowerCase().includes(resourceFilters.text.toLowerCase())
          : false))
    );
  });

  const handleChangeResourceFilters = (new_filters: ResourceFilterContext) => {
    setResourceFilters(new_filters);
  };

  // Mutate resources metadata array with updated or new data.
  const handleUpdateResourcesMetadata = (updated_resource_metadata: ResourceMetadata): void => {
    setResourcesMetadata((current_resources_metadata: Array<ResourceMetadata>) => {
      const existing_index: number = current_resources_metadata.findIndex(
        (resource_metadata: ResourceMetadata) =>
          resource_metadata.id === updated_resource_metadata.id
      );

      if (existing_index > -1) {
        const updated_array: Array<ResourceMetadata> = [...current_resources_metadata];
        updated_array[existing_index] = updated_resource_metadata;
        return updated_array;
      } else {
        return [...current_resources_metadata, updated_resource_metadata];
      }
    });
  };

  const fetchResources = async (): Promise<void> => {
    try {
      // Load resources from database.
      if (group && company) {
        const resources: Array<Resource> = await getResources();
        // Load resources to memory.
        setResources(resources);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ResourceCentreHome failed on an unknown error while calling fetchResources.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch Resources. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    }
  };

  const fetchResourcesMetadata = async (): Promise<void> => {
    try {
      // Load resources from database.
      if (group && company) {
        const resource_metadata: Array<ResourceMetadata> = await getResourceMetadata({
          group_id: group.id,
          company_id: company.id
        });
        // Load resources to memory.
        setResourcesMetadata(resource_metadata);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ResourceCentreHome failed on an unknown error while calling fetchResourcesMetadata.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch Resources Metadata. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    }
  };

  React.useEffect(() => {
    (async () => {
      setResourcesLoading(true);
      try {
        setResources([]);
        setResourcesMetadata([]);
        await fetchResources();
        await fetchResourcesMetadata();
      } catch (err: unknown) {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: ResourceCentreHome failed on an unknown error while initialising.',
            null,
            tracking_id
          )
        );
        setFeedbackData({
          message: `An error occurred. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      } finally {
        setResourcesLoading(false);
      }
    })();
  }, [group, company]);

  React.useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'exports.export_data')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box>
      <Grid container sx={{ textAlign: 'left' }}>
        <Grid size={12}>
          <ResourceCentreContext
            resources={resources}
            filters_context={resourceFilters}
            handleFiltersChange={handleChangeResourceFilters}
          />
        </Grid>
        <Grid size={12}>
          <ResourceCentreDisplayGrid
            resources={filtered_resources}
            resources_metadata={resourcesMetadata}
            resources_loading={resourcesLoading}
            handleUpdateResourcesMetadata={handleUpdateResourcesMetadata}
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default ResourceCentreHome;
